import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import {
  Star as StarIcon
} from '@mui/icons-material';
import FavoriteTeamsBySport from './FavoriteTeamsBySport';
import FavoriteTeamService from '../services/favoriteTeamService';
import { useAuth } from '../contexts/AuthContext';

const FavoriteTeamsSection = () => {
  const { currentUser } = useAuth();
  const [favoriteTeams, setFavoriteTeams] = useState({});
  const [favoriteTeamLoading, setFavoriteTeamLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  useEffect(() => {
    loadFavoriteTeams();
  }, [currentUser]);

  const loadFavoriteTeams = async () => {
    try {
      setFavoriteTeamLoading(true);
      setError(null);
      const currentFavoriteTeams = await FavoriteTeamService.getFavoriteTeams();
      setFavoriteTeams(currentFavoriteTeams);
    } catch (error) {
      console.error('Error loading favorite teams:', error);
      setError('Failed to load favorite teams.');
    } finally {
      setFavoriteTeamLoading(false);
    }
  };

  const handleFavoriteTeamChange = async (sport, team) => {
    try {
      setFavoriteTeamLoading(true);
      setError(null);
      
      // The actual save is handled by FavoriteTeamsBySport component
      // We just need to update our local state and show success
      const teamName = team?.name || null;
      setFavoriteTeams(prev => ({
        ...prev,
        [sport]: teamName
      }));
      
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 2000);
      
    } catch (error) {
      console.error('Error updating favorite team:', error);
      setError('Failed to save favorite team preference.');
    } finally {
      setFavoriteTeamLoading(false);
    }
  };

  return (
    <>
      {/* Success Alert */}
      {showSaveSuccess && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => setShowSaveSuccess(false)}
        >
          Favorite team saved successfully!
          {currentUser ? ' Changes synced to your account.' : ' Changes saved locally.'}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Favorite Teams Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <StarIcon sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="h6">
              Favorite Teams
            </Typography>
            {favoriteTeamLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your favorite teams will appear first for easier access
            {!currentUser && '. Sign in to sync your favorite teams across devices'}
          </Typography>
          
          <FavoriteTeamsBySport
            key={currentUser?.uid || 'anonymous'}
            disabled={favoriteTeamLoading || !currentUser}
            onChange={handleFavoriteTeamChange}
          />
          
          {!currentUser && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Please sign in to save your favorite team preferences.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default FavoriteTeamsSection;
